import { Flex, HStack, IconButton, Select } from "@chakra-ui/react";
import { getMonth, getYear } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import type { ReactDatePickerCustomHeaderProps } from "react-datepicker";

interface Props extends ReactDatePickerCustomHeaderProps {
  locale: string;
}

const getYears = () => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 18 }, (_, i) => currentYear - 2 + i);
};

// Year is irrelevant for month name formatting — only month index matters
const getMonthLabels = (locale: string): string[] => {
  const fmt = new Intl.DateTimeFormat(locale, { month: "long" });
  return Array.from({ length: 12 }, (_, i) => {
    const label = fmt.format(new Date(2000, i, 1));
    return label.charAt(0).toUpperCase() + label.slice(1);
  });
};

export const DatePickerHeader = ({
  date,
  changeYear,
  changeMonth,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
  locale,
}: Props) => {
  const { t } = useTranslation();
  const months = useMemo(() => getMonthLabels(locale), [locale]);
  const years = useMemo(getYears, []);

  return (
    <Flex align="center" justify="space-between" px={2} pb={2}>
      <IconButton
        aria-label={t("Previous month")}
        icon={<FaChevronLeft />}
        onClick={decreaseMonth}
        isDisabled={prevMonthButtonDisabled}
        variant="ghost"
        size="sm"
      />
      <HStack spacing={1.5} flex="1" minW="0" mx={1}>
        <Select
          size="sm"
          fontWeight={600}
          flex="1"
          minW="0"
          value={getMonth(date)}
          onChange={(e) => changeMonth(Number(e.target.value))}
          aria-label={t("Select month") ?? undefined}
          bg="smBackground"
          borderColor="smInputBorder"
          borderRadius="md"
          cursor="pointer"
        >
          {months.map((label, i) => (
            <option key={i} value={i}>{label}</option>
          ))}
        </Select>
        <Select
          size="sm"
          fontWeight={600}
          w="5.25rem"
          flexShrink={0}
          value={getYear(date)}
          onChange={(e) => changeYear(Number(e.target.value))}
          aria-label={t("Select year") ?? undefined}
          bg="smBackground"
          borderColor="smInputBorder"
          borderRadius="md"
          cursor="pointer"
        >
          {years.map((year) => (
            <option key={year} value={year}>{year}</option>
          ))}
        </Select>
      </HStack>
      <IconButton
        aria-label={t("Next month")}
        icon={<FaChevronRight />}
        onClick={increaseMonth}
        isDisabled={nextMonthButtonDisabled}
        variant="ghost"
        size="sm"
      />
    </Flex>
  );
};
